import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MultiselectObjectManagerComponentBase } from '../../../core/component/multiselect-object-manager/multiselect-object-manager.component';
import { getMonthName } from '../../../core/helper/month';
import { getTranslation } from '../../../core/helper/translation';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { LocalDate } from '../../../core/model/local-date';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ContactBalanceTransaction } from '../../../rent/model/contact-balance-transaction';
import { ContactBalanceTransactionCommand } from '../../command/contact-balance-transaction';
import { ContactBalanceTransactionCommandHandlerResolver } from '../../service/command/contact-balance-transaction-command-handler-resolver.service';
import { ContactBalanceTransactionService } from '../../service/data/contact-balance-transaction.service';
import { CompanyService } from '../../../rent/service/data/company.service';
import { LanguageService } from '../../../core/service/language.service';

@Component({
    selector: 'app-contact-balance-transaction-list',
    templateUrl: './contact-balance-transaction-list.component.html',
    styleUrls: ['./contact-balance-transaction-list.component.scss'],
    providers: [ContactBalanceTransactionCommandHandlerResolver],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContactBalanceTransactionListComponent
  extends MultiselectObjectManagerComponentBase<ContactBalanceTransaction>
  implements OnInit
{
  constructor(
    private _dataService: ContactBalanceTransactionService,
    private readonly _languageService: LanguageService,
    private readonly _companyService: CompanyService,
    cdf: ColumnDefinitionFactory,
    commandDispatcher: CommandDispatcher,
    commandHandlerResolver: ContactBalanceTransactionCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService
  ) {
    super(_dataService, commandDispatcher, commandHandlerResolver, messageService, navigationService);
    this.columnDefinitions = [
      cdf.createDate('ACCOUNT.ACCOUNTING_DATE', 'businessTransactionDate'),
      cdf.create('VERIFICATION.NUMBER', 'verificationNumber', ColumnVisibility.optional),
      cdf.create('ACCOUNT_TRANSACTION.DESCRIPTION', 'description'),
      cdf.createMoney('COMMON.AMOUNT', 'amount'),
      cdf.create('COMMON.CONTACT', 'contactNumberAndName'),
      cdf.create('COMMON.COMPANY', 'companyNumberAndName'),
      cdf.create('CONTACT_BALANCE_TRANSACTION.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      cdf.create('CONTACT_BALANCE_TRANSACTION.INVOICE_NUMBER', 'invoiceNumber', ColumnVisibility.optional),
      cdf.createDate('INCORRECT_PAYMENT.CREATION_DATE', 'createdDate', ColumnVisibility.optional),
    ];

    this.menuDefinition = [new MenuItemDefinition('LEASE_AGREEMENT.COMMAND.EXPORT', ContactBalanceTransactionCommand.export)];
    this.preferencesKey = 'invoice.contact-balance-transaction-list.list';
  }

  columnDefinitions: ColumnDefinition[];
  contextMenuDefinition: MenuItemDefinition[];
  itemsSource: ContactBalanceTransaction[];

  ngOnInit() {
    let tran = this._languageService.getCurrentTranslations();

    this.AddSubscription(
      this._dataService.getFilterDefinition().subscribe(definition => {
        this.defaultFilters = definition.defaultFields;

        const showFromDates = definition.dateLimits.map(x => {
          return {
            caption: `${x.month ? getTranslation(tran, getMonthName(x.month)) : ''} ${x.year ? x.year + ' ' : ''}`,
            value: new LocalDate(x.year, x.month, 1).toISOString(),
          };
        });
        showFromDates.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: null });
        const showToDates = definition.dateLimits.map(x => {
          return {
            caption: `${x.month ? getTranslation(tran, getMonthName(x.month)) : ''} ${x.year ? x.year + ' ' : ''}`,
            value: new LocalDate(x.year, x.month, LocalDate.daysInMonth(x.year, x.month)).toISOString(),
          };
        });
        showToDates.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: null });
        const yesNo = [
          { caption: 'NO_TEXT', value: false },
          { caption: 'YES_TEXT', value: true },
        ];

        this.filterDefinition = [
          new FilterDefinitionItem(
            'COMPANY.LIST_HEADER',
            'company',
            definition.company,
            this._companyService,
            definition.selectedFields.includes('company')
          ),
          new FilterDefinitionItem(
            'VERIFICATION.ACCOUNTING_DATE_FROM',
            'fromDate',
            definition.fromDate,
            showFromDates,
            definition.selectedFields.includes('fromDate')
          ),
          new FilterDefinitionItem(
            'VERIFICATION.ACCOUNTING_DATE_TO',
            'toDate',
            definition.toDate,
            showToDates,
            definition.selectedFields.includes('toDate')
          ),
          new FilterDefinitionItem(
            'CONTACT_BALANCE_TRANSACTION.AMOUNT',
            'amount',
            definition.amount,
            null,
            definition.selectedFields.includes('amount')
          ),
          new FilterDefinitionItem(
            'CONTACT_BALANCE_TRANSACTION.HIDE_ZERO_BALANCE',
            'hideZeroBalance',
            definition.hideZeroBalance,
            yesNo,
            definition.selectedFields.includes('hideZeroBalance')
          ),
          new FilterDefinitionItem(
            'TENANT.NAME',
            'contactName',
            definition.contactName,
            null,
            definition.selectedFields.includes('contactName')
          ),
        ];
      })
    );
  }
}
